import { TrainingOrder } from "../types/castle";
import { UnitCatalogEntry, UnitClassRu } from "../types/army";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { UnitIcon } from "./UnitIcon";
import { SpeedupOrderKind, useApplySpeedup } from "../api/inventory";
import { useTrainingAdminSpeedup } from "../api/army";

// Inventory speedup tiers, same set as in BuildingDetailSheet.
const tiers: Array<{ id: string; label: string; seconds: number }> = [
  { id: "spd-1m",  label: "+1м",  seconds: 60 },
  { id: "spd-5m",  label: "+5м",  seconds: 300 },
  { id: "spd-30m", label: "+30м", seconds: 1800 },
  { id: "spd-1h",  label: "+1ч",  seconds: 3600 },
  { id: "spd-8h",  label: "+8ч",  seconds: 28800 },
  { id: "spd-24h", label: "+24ч", seconds: 86400 },
];

// Free admin presets — same as BuildingDetailSheet, for owner/test tuning.
const adminPresets: Array<{ label: string; seconds: number }> = [
  { label: "+1м",  seconds: 60 },
  { label: "+5м",  seconds: 300 },
  { label: "+30м", seconds: 1800 },
  { label: "+1ч",  seconds: 3600 },
];

interface Props {
  order: TrainingOrder;
  unit: UnitCatalogEntry | undefined;
  // BuildingType that's doing the training (Barracks/ArcheryRange/MageTower/
  // SignatureForge). Required by the speedup endpoint to disambiguate when
  // multiple training queues are active at once.
  buildingType: number;
  inventory: Record<string, number>;
  now: Date;
  onClose: () => void;
}

export function TrainingSpeedupModal({
  order,
  unit,
  buildingType,
  inventory,
  now,
  onClose,
}: Props) {
  const apply = useApplySpeedup();
  const adminSpeedup = useTrainingAdminSpeedup();
  const remaining = secondsUntil(order.finishesAt, now);

  const onApply = (itemId: string) => {
    hapticImpact("medium");
    apply.mutate(
      { itemId, orderKind: SpeedupOrderKind.Training, buildingType },
      {
        onSuccess: () => {
          hapticNotification("success");
          // Close on success — the underlying castle query will refetch,
          // and if the order is gone (speedup completed it) there's nothing
          // to come back to anyway.
          onClose();
        },
        onError: () => hapticNotification("error"),
      },
    );
  };

  const onAdminSpeedup = (seconds: number) => {
    hapticImpact("light");
    adminSpeedup.mutate(
      { buildingType, seconds },
      {
        onSuccess: () => {
          hapticNotification("success");
          // Don't auto-close on free speedups so the owner can keep stacking
          // them while tuning — handy when shaving down long training queues.
        },
        onError: () => hapticNotification("error"),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-2xl bg-tg-bg p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-tg-secondaryBg">
              <UnitIcon unitId={order.unitId} cls={unit?.cls} size={56} rounded="xl" />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-semibold leading-tight">
                {unit?.name ?? order.unitId}
              </div>
              <div className="mt-0.5 text-xs text-tg-hint">
                {unit ? UnitClassRu[unit.cls] : ""} · ×{formatNumber(order.count)}
              </div>
              <div className="mt-0.5 text-xs text-tg-hint">
                Осталось: {formatDuration(remaining)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-tg-hint"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div className="mb-1 text-[10px] uppercase tracking-wide text-tg-hint">
          Применить ускоритель из инвентаря
        </div>
        <div className="grid grid-cols-3 gap-2">
          {tiers.map((t) => {
            const count = inventory[t.id] ?? 0;
            const disabled = apply.isPending || remaining <= 0 || count <= 0;
            return (
              <button
                key={t.id}
                type="button"
                disabled={disabled}
                onClick={() => onApply(t.id)}
                className={`flex flex-col items-center gap-0.5 rounded-lg bg-tg-secondaryBg px-2 py-2 text-xs disabled:opacity-30 ${
                  count > 0 ? "ring-1 ring-tg-accent/30" : ""
                }`}
              >
                <span className="font-medium">{t.label}</span>
                <span className="text-[10px] text-tg-hint tabular-nums">
                  x{count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-3 grid grid-cols-4 gap-1">
          {adminPresets.map((p) => (
            <button
              key={p.seconds}
              type="button"
              disabled={adminSpeedup.isPending || remaining <= 0}
              onClick={() => onAdminSpeedup(p.seconds)}
              className="rounded-lg bg-tg-bg/40 px-1 py-1 text-[10px] text-tg-hint disabled:opacity-30"
            >
              free {p.label}
            </button>
          ))}
        </div>

        {(apply.error || adminSpeedup.error) && (
          <div className="mt-2 text-xs text-tg-destructive">
            {(apply.error as { response?: { data?: { error?: string } } })?.response?.data?.error
              ?? (adminSpeedup.error as { response?: { data?: { error?: string } } })?.response?.data?.error
              ?? "Ошибка"}
          </div>
        )}
      </div>
    </div>
  );
}
